import { c as _c } from "react/compiler-runtime";
import React, { FunctionComponent, ComponentProps, useState, useCallback } from 'react';
import { ButtonStandard, ButtonStandardType } from 'record-it-components/button/ButtonStandard/ButtonStandard';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { IconDeprecated } from 'record-it-components/misc/IconDeprecated/IconDeprecated';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { EntityName } from 'record-it-time-common/EntityName';
import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { UserGroup } from '../../../classes/entities/UserGroup/userGroupTypes';
import { UserGroupToUser } from '../../../classes/entities/UserGroupToUser/userGroupToUserTypes';
import { useEntities } from '../../../hooks/entity/useEntities/useEntities';
import { useEntityRepository } from '../../../hooks/entity/useEntityRepository';
import { UserSelect, UserSelectProps } from '../../input/UserSelect/UserSelect';
import styles from './UserGroupToUsersWidget.module.css';
import { UserGroupToUsersWidgetUser } from './UserGroupToUsersWidgetUser/UserGroupToUsersWidgetUser';
export type UserGroupToUsersWidgetProps = PropsWithoutChildren<ComponentProps<'div'>> & {
  userGroup: UserGroup;
};
export const UserGroupToUsersWidget: FunctionComponent<UserGroupToUsersWidgetProps> = React.memo(props => {
  const $ = _c(40);
  let t0;
  if ($[0] !== props) {
    t0 = prepareProps({
      props
    });
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let remainingProps;
  let userGroup;
  if ($[2] !== t0) {
    ({
      userGroup,
      ...remainingProps
    } = t0);
    $[2] = t0;
    $[3] = remainingProps;
    $[4] = userGroup;
  } else {
    remainingProps = $[3];
    userGroup = $[4];
  }
  const [userIdToAdd, setUserIdToAdd] = useState(null);
  const userGroupToUsers = useEntities(EntityName.UserGroupToUser, "getByUserGroupId", userGroup.id);
  const userGroupToUserRepository = useEntityRepository(EntityName.UserGroupToUser);
  let t1;
  if ($[5] !== userGroup.id || $[6] !== userGroupToUserRepository || $[7] !== userIdToAdd) {
    t1 = () => {
      assertNotNullOrUndefined(userIdToAdd, "no userIdToAdd selected");
      userGroupToUserRepository.create({
        userId: userIdToAdd,
        ownerUserGroupId: userGroup.id
      });
      setUserIdToAdd(null);
    };
    $[5] = userGroup.id;
    $[6] = userGroupToUserRepository;
    $[7] = userIdToAdd;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const handleAddUserClick = useEventCallback(t1);
  let t2;
  if ($[9] !== userGroupToUserRepository) {
    t2 = userGroupToUser => {
      userGroupToUserRepository.delete(userGroupToUser);
    };
    $[9] = userGroupToUserRepository;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const handleDelete = useEventCallback(t2);
  let t3;
  if ($[11] !== userGroupToUserRepository) {
    t3 = modifiedUserGroupUser => {
      userGroupToUserRepository.update(modifiedUserGroupUser);
    };
    $[11] = userGroupToUserRepository;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const handleUserGroupUserChanged = useEventCallback(t3);
  let t4;
  if ($[13] !== userGroupToUsers) {
    t4 = users => users.filter(u => !userGroupToUsers.find(ugu => ugu.userId === u.id));
    $[13] = userGroupToUsers;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const filterUsers = t4;
  let t5;
  if ($[15] !== handleDelete || $[16] !== handleUserGroupUserChanged || $[17] !== userGroup.id || $[18] !== userGroupToUsers) {
    let t6;
    if ($[20] !== handleDelete || $[21] !== handleUserGroupUserChanged || $[22] !== userGroup.id) {
      t6 = userGroupToUser_0 => <UserGroupToUsersWidgetUser key={userGroupToUser_0.userId} userGroupId={userGroup.id} userGroupToUser={userGroupToUser_0} onDelete={handleDelete} onUserGroupUserChanged={handleUserGroupUserChanged} data-testid="UserGroupToUsersWidget.User" />;
      $[20] = handleDelete;
      $[21] = handleUserGroupUserChanged;
      $[22] = userGroup.id;
      $[23] = t6;
    } else {
      t6 = $[23];
    }
    t5 = userGroupToUsers.map(t6);
    $[15] = handleDelete;
    $[16] = handleUserGroupUserChanged;
    $[17] = userGroup.id;
    $[18] = userGroupToUsers;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[24] !== t5) {
    t6 = <div className={styles.userList}>{t5}</div>;
    $[24] = t5;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] !== filterUsers || $[27] !== userIdToAdd) {
    t7 = <UserSelect label="" value={userIdToAdd} onValueChanged={setUserIdToAdd} filterUsers={filterUsers} data-testid="UserGroupToUsersWidget.UserSelect" />;
    $[26] = filterUsers;
    $[27] = userIdToAdd;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  const t8 = !userIdToAdd;
  let t9;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <IconDeprecated name="plus-circle" />;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== handleAddUserClick || $[31] !== t8) {
    t10 = <ButtonStandard type={ButtonStandardType.Primary} onClick={handleAddUserClick} className={styles.addUserButton} disabled={t8} data-testid="UserGroupToUsersWidget.AddUserButton">{t9}</ButtonStandard>;
    $[30] = handleAddUserClick;
    $[31] = t8;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== t10 || $[34] !== t7) {
    t11 = <div className={styles.addUserWrapper}>{t7}{t10}</div>;
    $[33] = t10;
    $[34] = t7;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== remainingProps || $[37] !== t11 || $[38] !== t6) {
    t12 = <div {...remainingProps}>{t6}{t11}</div>;
    $[36] = remainingProps;
    $[37] = t11;
    $[38] = t6;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  return t12;
});